<script setup lang="ts">
import { computed, ref, watch, inject, type Ref } from 'vue';
import { paymentsApi, type Payment, type PDFPayment, type XlsxPayments } from '@/api/payments';
import { useQuery, useMutation } from '@tanstack/vue-query';
import { createQueryParams } from '@/utils/query-params';
import { useTableFilter } from '@/composables/useTableFilter';
import { usePaymentsStore } from '@/stores/payments';
import { useGroupFilterStore } from '@/stores/group-filter';
import { storeToRefs } from 'pinia';
import { formatCurrency } from '@/utils/currency';
import PaymentsListDetail from '@/components/payments/payments-list-detail.vue';
import { paymentPdfsApi } from '@/api/payment-pdfs';
import { usePermissions } from '@/composables/usePermissions';
import { useDateRangeValidation } from '@/composables/useDateRangeValidation';
import { useLinkedDateRange } from '@/composables/useLinkedDateRange';
import { useDebouncedRef } from '@/composables/useDebouncedRef';
import { type SerializedUser } from '@/api/users';
import { useAnalytics, AnalyticsEvents } from '@/composables/useAnalytics';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { useMobile } from '@/composables/useMobile';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import PaymentCard from '@/components/payments/payment-card.vue';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import FilterMultiSelect from '@/components/filter-multi-select.vue';
import BulkExportButton from '@/components/bulk-export-button.vue';
import PaymentEmailsButton from '@/components/payments/payment-emails-button.vue';
import { useAccessibleCareers } from '@/composables/useAccessibleCareers';
import { employmentContractTypes } from '@/utils/employment-contract-types';

const { hasPermission } = usePermissions();
const { trackPaymentEvent } = useAnalytics();
const { tableProps } = useResponsiveTable({ alwaysShowFooter: true });
const { mdAndUp } = useMobile();

const props = defineProps<{
  formattedDates: {
    fromDate: string;
    toDate: string;
  };
}>();

const store = usePaymentsStore();
if (!store.fromDate) store.fromDate = props.formattedDates.fromDate;
if (!store.toDate) store.toDate = props.formattedDates.toDate;

const { groupIds } = storeToRefs(useGroupFilterStore());
const { accessibleCareers } = useAccessibleCareers();

// Page-local by design: not persisted nor shared with the dashboard filter stores.
const careerIn = ref<string[]>([]);
const employmentContractTypeIn = ref<string[]>([]);
const careerOptions = computed(() => [
  ...accessibleCareers.value,
  { title: 'Sin especialidad', value: 'without_career' },
]);

const filters = ref<{
  fromDate: string;
  toDate: string;
}>({
  fromDate: store.fromDate,
  toDate: store.toDate,
});

const tableSortBy = ref<readonly { key: string; order: 'asc' | 'desc' }[]>([]);

const queryParams = computed(() => createQueryParams({
  ...filters.value,
  groupIdIn: groupIds.value,
  caregiverCareerIn: careerIn.value,
  employmentContractTypeIn: employmentContractTypeIn.value,
}));
const { isValidDateRange, dateRangeError } = useDateRangeValidation(filters);
// Each request loads every shift of the period on the server: stepping through
// the date inputs asks once the filters settle, not once per step.
const settledQueryParams = useDebouncedRef(queryParams);
// The range is validated live but sent settled: until both match, the settled
// params can still carry a range the screen already refused.
const isSettledRangeValid = computed(
  () => isValidDateRange.value && settledQueryParams.value === queryParams.value,
);

// A browser session gets 3 summaries per 10 s (config/initializers/rack_attack.rb):
// a list fetched within that window is current and Filtrar does not ask again.
const SUMMARY_FRESH_MS = 10_000;

const { data: paymentsData, refetch, isFetching, isStale } = useQuery({
  queryKey: ['payments', settledQueryParams],
  queryFn: () => paymentsApi.index(settledQueryParams.value),
  enabled: isSettledRangeValid,
  staleTime: SUMMARY_FRESH_MS,
});
const payments = computed<Payment[]>(() => paymentsData.value ?? []);

// Filtrar applies dates that have not settled yet. Settled dates were already
// asked for (a person rarely clicks within the debounce), so it then reloads
// only a list that is no longer fresh.
function applyFilters() {
  if (settledQueryParams.value !== queryParams.value) {
    settledQueryParams.value = queryParams.value;
  } else if (isStale.value) {
    refetch();
  }
}

watch(filters, () => {
  store.fromDate = filters.value.fromDate;
  store.toDate = filters.value.toDate;
}, { deep: true });

useLinkedDateRange(filters);

const staticHeaders = [
  { title: 'Profesional', value: 'caregiver.fullName', sortable: true },
  { title: 'Turnos asignados', value: 'shiftsCount', sortable: true },
  { title: 'Turnos Marcados', value: 'checkedInShiftsCount', sortable: true },
  { title: 'Monto Bruto', value: 'totalCost', sortable: true },
  { title: 'Monto Líquido', value: 'cost', sortable: true },
  { title: 'Ajustes', value: 'totalPaymentMovements', sortable: true },
  { title: 'Pago a Profesional', value: 'amountPendingToPay', sortable: true },
  { title: 'Ver Detalle', value: 'actions', sortable: false },
];

const totalShiftsCount = computed(
  () => payments.value.reduce((acc: number, payment: Payment) => acc + payment.shiftsCount, 0),
);
const totalCheckedInShiftsCount = computed(
  () => payments.value.reduce((acc: number, payment: Payment) => acc + payment.checkedInShiftsCount, 0),
);
const totalCost = computed(
  () => payments.value.reduce((acc: number, payment: Payment) => acc + payment.cost, 0),
);
const totalAmount = computed(
  () => payments.value.reduce((acc: number, payment: Payment) => acc + payment.totalCost, 0),
);
const totalPaymentMovements = computed(
  () => payments.value.reduce((acc: number, payment: Payment) => acc + payment.totalPaymentMovements, 0),
);
const totalAmountPendingToPay = computed(
  () => payments.value.reduce((acc: number, payment: Payment) => acc + payment.amountPendingToPay, 0),
);

const snackbarAlert = ref(false);
function downloadCaregiverPDF(caregiverPayment: Payment) {
  snackbarAlert.value = true;
  paymentsApi.show(caregiverPayment.caregiver.id, queryParams.value, 'pdf').then((response: PDFPayment) => {
    const { headers: responseHeaders, data, data: { type } } = response;
    const url = window.URL.createObjectURL(new Blob([data], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.download = responseHeaders['content-disposition'].split('"')[1];
    link.click();
  });
}

const emailSnackbarAlert = ref(false);
const emailError = ref(false);
function sendCaregiverPaymentsViaEmail(caregiverPayment: Payment) {
  emailError.value = false;
  emailSnackbarAlert.value = true;
  paymentsApi.show(caregiverPayment.caregiver.id, queryParams.value, 'json').catch(() => {
    emailSnackbarAlert.value = false;
    emailError.value = true;
  });
}

function localizedDate(date: string) {
  return date.split('-').reverse().join('/');
}

const search = ref('');

const customFilter = useTableFilter([
  'caregiver.fullName',
  'shiftsCount',
  'checkedInShiftsCount',
  'totalCost',
  'cost',
  'totalPaymentMovements',
  'amountPendingToPay',
]);

const xlsxDownloadSnackbarAlert = ref(false);
function downloadPaymentsXlsx() {
  xlsxDownloadSnackbarAlert.value = true;

  let downloadQueryParams = queryParams.value;
  if (tableSortBy.value.length > 0) {
    const sortBy = JSON.stringify(tableSortBy.value);
    downloadQueryParams = `${queryParams.value}&sort_by=${encodeURIComponent(sortBy)}`;
  }

  paymentsApi.indexAsXlsx(downloadQueryParams).then((response: XlsxPayments) => {
    const { headers: responseHeaders, data, data: { type } } = response;
    const url = window.URL.createObjectURL(new Blob([data], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.download = responseHeaders['content-disposition'].split('"')[1];
    link.click();
  });
}

const whatsappSnackbarAlert = ref(false);
const errorSnackbarAlert = ref(false);
const errorMessage = ref('');

function generateWhatsappMessage(payment: Payment, pdfUrl: string) {
  const message = 'Hola, envío el detalle de los servicios prestados:\n' +
    '------------------\n' +
    `*Profesional*: ${payment.caregiver.fullName}\n` +
    `*Desde*: ${localizedDate(filters.value.fromDate)}\n` +
    `*Hasta*: ${localizedDate(filters.value.toDate)}\n` +
    '------------------\n' +
    'Puedes ver el detalle en PDF en el siguiente link:\n' +
    `${pdfUrl}`;

  return encodeURIComponent(message);
}

const { mutateAsync: createPaymentPdf, isPending: isCreatingPaymentPdf } = useMutation({
  mutationFn: (payment: Payment) => paymentPdfsApi.create({
    caregiverId: payment.caregiver.id,
    fromDate: filters.value.fromDate,
    toDate: filters.value.toDate,
  }),
});

function clickWhatsappLink(phoneNumber: string, message: string) {
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
  const link = document.createElement('a');
  link.href = whatsappUrl;
  link.setAttribute('target', '_blank');
  link.setAttribute('rel', 'noopener noreferrer');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

async function sendWhatsappMessage(payment: Payment) {
  whatsappSnackbarAlert.value = true;
  try {
    const paymentPdf = await createPaymentPdf(payment);
    trackPaymentEvent(AnalyticsEvents.payments.whatsappSent, {
      period: `${filters.value.fromDate}-${filters.value.toDate}`,
      caregiverId: payment.caregiver.id,
    });
    const pdfUrl = `${window.location.origin}/pp/${paymentPdf.publicId}`;
    const message = generateWhatsappMessage(payment, pdfUrl);
    clickWhatsappLink(payment.caregiver.phoneNumber, message);
  } catch {
    errorMessage.value = 'Hubo un error generando el mensaje de WhatsApp. Por favor, inténtalo nuevamente.';
    errorSnackbarAlert.value = true;
  } finally {
    whatsappSnackbarAlert.value = false;
  }
}

const currentUser = inject<Ref<SerializedUser>>('currentUser');

const headers = computed(() => staticHeaders.filter(header => {
  if (header.value === 'checkedInShiftsCount') {
    return currentUser?.value?.companyIncludeCheckedInShiftsOnly;
  } else if (header.value === 'actions') {
    return hasPermission.value('payments', 'show');
  }

  return true;
}));

function filterPaymentsBySearch(list: Payment[], term: string): Payment[] {
  if (!term) return list;

  const query = term.toLowerCase();

  return list.filter((payment: Payment) => [
    payment.caregiver.fullName,
    String(payment.shiftsCount),
    String(payment.totalCost),
    String(payment.cost),
    String(payment.totalPaymentMovements),
    String(payment.amountPendingToPay),
  ].join(' ').toLowerCase().includes(query));
}
const filteredPayments = computed(() => filterPaymentsBySearch(payments.value, search.value));

const paymentCaregiverIds = computed(() => filteredPayments.value.map((payment) => payment.caregiver.id));

// Detail + email confirmation are hoisted so the desktop table rows and the
// mobile cards can both drive them.
const selectedCaregiverId = ref<number | null>(null);
const showDetailDialog = ref(false);
// Looked up from the query data so the open dialog stays fresh after
// payment movement mutations refetch the payments.
const selectedPayment = computed(
  () => payments.value.find((payment: Payment) => payment.caregiver.id === selectedCaregiverId.value) ?? null,
);
function openDetail(payment: Payment) {
  selectedCaregiverId.value = payment.caregiver.id;
  showDetailDialog.value = true;
}

const emailPayment = ref<Payment | null>(null);
const showEmailDialog = ref(false);
function openEmailDialog(payment: Payment) {
  emailPayment.value = payment;
  showEmailDialog.value = true;
}
function confirmSendEmail() {
  if (emailPayment.value) sendCaregiverPaymentsViaEmail(emailPayment.value);
  showEmailDialog.value = false;
}

// Bulk send (desktop table only): the rows ticked, for the period on screen. Any
// filter or date change clears the selection, so it never outlives the rows it
// was made on.
const canSendBulkEmails = computed(() => mdAndUp.value && hasPermission.value('payments', 'show'));
const selectedCaregiverIds = ref<number[]>([]);
const selectedPayments = computed(() => {
  const ids = new Set(selectedCaregiverIds.value);

  return payments.value.filter((payment: Payment) => ids.has(payment.caregiver.id));
});
watch(queryParams, () => {
  selectedCaregiverIds.value = [];
});
</script>
<template>
  <v-card class="w-100">
    <v-toolbar
      color="white"
      flat
    >
      <v-toolbar-title class="font-weight-bold">
        Pagos
      </v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <v-row class="justify-start d-flex align-center">
        <v-text-field
          v-model="filters.fromDate"
          class="px-2 v-col-12 v-col-sm-6 v-col-md-3"
          type="date"
          label="Fecha desde"
          variant="outlined"
          density="compact"
          :error-messages="dateRangeError"
        />
        <v-text-field
          v-model="filters.toDate"
          class="px-2 v-col-12 v-col-sm-6 v-col-md-3"
          type="date"
          label="Fecha hasta"
          variant="outlined"
          density="compact"
          :error-messages="dateRangeError"
        />
        <v-btn
          class="mb-6 mr-1 bg-primary"
          prepend-icon="mdi-magnify"
          :disabled="isFetching || !isValidDateRange"
          @click="applyFilters"
        >
          Filtrar
        </v-btn>
        <v-spacer />
        <v-btn
          icon="mdi-microsoft-excel"
          class="mb-6 mr-2"
          color="primary"
          variant="text"
          density="comfortable"
          :disabled="isFetching || xlsxDownloadSnackbarAlert"
          @click="downloadPaymentsXlsx"
        >
          <v-icon icon="mdi-microsoft-excel" />
          <v-tooltip
            activator="parent"
            location="top"
          >
            Descargar pagos en Excel
          </v-tooltip>
        </v-btn>
        <v-snackbar
          v-model="xlsxDownloadSnackbarAlert"
          :timeout="2000"
          color="primary"
          rounded="pill"
        >
          Se está generando el archivo Excel, puede tomar unos segundos...
        </v-snackbar>
        <bulk-export-button
          v-if="hasPermission('payments', 'show')"
          export-type="payments"
          :record-ids="paymentCaregiverIds"
          :from-date="filters.fromDate"
          :to-date="filters.toDate"
          :disabled="isFetching || !isValidDateRange"
        />
        <payment-emails-button
          v-if="canSendBulkEmails"
          :payments="selectedPayments"
          :from-date="filters.fromDate"
          :to-date="filters.toDate"
          :disabled="isFetching || !isValidDateRange"
          @sent="selectedCaregiverIds = []"
        />
      </v-row>
      <v-row class="justify-end d-flex align-center">
        <v-text-field
          v-model="search"
          label="Buscar"
          class="px-2 v-col-12 v-col-md-3"
          prepend-inner-icon="mdi-magnify"
          single-line
          variant="underlined"
          hide-details
        />
        <filter-multi-select
          v-model="careerIn"
          :items="careerOptions"
          label="Especialidades"
          hide-details
          class="px-2 v-col-12 v-col-sm-4 v-col-md-3"
        />
        <filter-multi-select
          v-model="employmentContractTypeIn"
          :items="employmentContractTypes"
          label="Tipo de contrato"
          hide-details
          class="px-2 v-col-12 v-col-sm-4 v-col-md-3"
        />
        <group-filter-select
          v-model="groupIds"
          hide-details
          class="px-2 v-col-12 v-col-sm-4 v-col-md-3"
        />
      </v-row>
    </v-card-text>

    <v-data-table
      v-if="mdAndUp"
      v-bind="tableProps"
      v-model="selectedCaregiverIds"
      v-model:sort-by="tableSortBy"
      :show-select="canSendBulkEmails"
      :item-value="(payment: Payment) => payment.caregiver.id"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="payments"
      :search="search"
      :custom-filter="customFilter"
      :loading="isFetching"
      loading-text="Cargando los pagos..."
      no-data-text="No hay pagos en este rango de fechas"
      multi-sort
    >
      <template #item.totalCost="{ item }">
        {{ formatCurrency(item.totalCost) }}
      </template>
      <template #item.cost="{ item }">
        {{ formatCurrency(item.cost) }}
      </template>
      <template #item.totalPaymentMovements="{ item }">
        {{ formatCurrency(item.totalPaymentMovements) }}
      </template>
      <template #item.amountPendingToPay="{ item }">
        <v-chip color="primary">
          {{ formatCurrency(item.amountPendingToPay) }}
        </v-chip>
      </template>
      <template #item.actions="{ item }">
        <v-btn
          icon="mdi-eye"
          size="small"
          class="me-2"
          @click="openDetail(item)"
        />
        <v-btn
          icon="mdi-printer"
          size="small"
          class="me-2"
          @click="downloadCaregiverPDF(item)"
        />
        <v-btn
          v-if="item.caregiver.email"
          icon
          size="small"
          class="me-2"
          @click="openEmailDialog(item)"
        >
          <v-icon icon="mdi-email-arrow-right" />
          <v-tooltip
            v-if="item.lastEmailSentAt"
            activator="parent"
            location="top"
          >
            Enviado hace {{ item.lastEmailSentAt }}
          </v-tooltip>
        </v-btn>
        <v-btn
          v-if="item.caregiver.phoneNumber"
          icon="mdi-whatsapp"
          size="small"
          class="me-2"
          :disabled="isCreatingPaymentPdf"
          @click="sendWhatsappMessage(item)"
        />
      </template>
      <template
        v-if="!search"
        #body.append
      >
        <tr>
          <th v-if="canSendBulkEmails" />
          <th class="font-weight-bold">
            Total
          </th>
          <th class="font-weight-bold">
            {{ totalShiftsCount }}
          </th>
          <th
            v-if="currentUser?.companyIncludeCheckedInShiftsOnly"
            class="font-weight-bold"
          >
            {{ totalCheckedInShiftsCount }}
          </th>
          <th class="font-weight-bold">
            {{ formatCurrency(totalAmount) }}
          </th>
          <th class="font-weight-bold">
            {{ formatCurrency(totalCost) }}
          </th>
          <th class="font-weight-bold">
            {{ formatCurrency(totalPaymentMovements) }}
          </th>
          <th>
            <v-chip color="grey-darken-3">
              {{ formatCurrency(totalAmountPendingToPay) }}
            </v-chip>
          </th>
        </tr>
      </template>
    </v-data-table>

    <!-- Mobile: card list -->
    <template v-else>
      <v-card
        variant="tonal"
        color="grey-darken-3"
        class="mx-3 mb-3 pa-3"
      >
        <div class="d-flex flex-wrap ga-4">
          <div>
            <div class="text-caption">
              Turnos
            </div>
            <div class="font-weight-bold">
              {{ totalShiftsCount }}
            </div>
          </div>
          <div>
            <div class="text-caption">
              Bruto
            </div>
            <div class="font-weight-bold">
              {{ formatCurrency(totalAmount) }}
            </div>
          </div>
          <div>
            <div class="text-caption">
              Líquido
            </div>
            <div class="font-weight-bold">
              {{ formatCurrency(totalCost) }}
            </div>
          </div>
          <div>
            <div class="text-caption">
              Pago total
            </div>
            <div class="font-weight-bold">
              {{ formatCurrency(totalAmountPendingToPay) }}
            </div>
          </div>
        </div>
      </v-card>
      <div class="d-flex flex-column ga-2 px-3 pb-3">
        <payment-card
          v-for="payment in filteredPayments"
          :key="payment.caregiver.id"
          :payment="payment"
          :can-show-document="hasPermission('payments', 'show')"
          @open="openDetail(payment)"
          @pdf="downloadCaregiverPDF(payment)"
          @email="openEmailDialog(payment)"
          @whatsapp="sendWhatsappMessage(payment)"
        />
        <p
          v-if="!filteredPayments.length"
          class="text-center text-medium-emphasis py-6"
        >
          {{ isFetching ? 'Cargando los pagos...' : 'No hay pagos en este rango de fechas' }}
        </p>
      </div>
    </template>
  </v-card>

  <responsive-dialog
    v-model="showDetailDialog"
    max-width="1400"
    scrollable
  >
    <payments-list-detail
      v-if="selectedPayment"
      :payment="selectedPayment"
      @close="showDetailDialog = false"
    />
  </responsive-dialog>

  <responsive-dialog
    v-model="showEmailDialog"
    max-width="800"
    scrollable
  >
    <v-card v-if="emailPayment">
      <v-card-title class="ml-2 align-baseline d-flex">
        Enviar correo de pago
        <v-spacer />
        <v-btn
          icon="mdi-window-close"
          variant="plain"
          @click="showEmailDialog = false"
        />
      </v-card-title>
      <v-card-text class="mt-4">
        Estás a punto de enviar un correo con el pago correspondiente al
        profesional <b>{{ emailPayment.caregiver.fullName }}</b> desde el <b>{{ localizedDate(filters.fromDate) }}</b>
        hasta el <b>{{ localizedDate(filters.toDate) }}</b>.
      </v-card-text>
      <v-card-text>
        Será enviado al profesional, al correo <b>{{ emailPayment.caregiver.email }}</b>.
      </v-card-text>
      <v-card-text v-if="emailPayment.lastEmailSentAt">
        El último correo de pago enviado para este profesional fue <b>hace {{ emailPayment.lastEmailSentAt }}</b>.
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          text="Enviar"
          color="primary"
          @click="confirmSendEmail"
        />
      </v-card-actions>
    </v-card>
  </responsive-dialog>
  <v-snackbar
    v-model="snackbarAlert"
    :timeout="2000"
    color="primary"
    rounded="pill"
  >
    Se está generando el PDF, puede tomar unos segundos...
  </v-snackbar>
  <v-snackbar
    v-model="emailSnackbarAlert"
    :timeout="2000"
    color="primary"
    rounded="pill"
  >
    Se está enviando el correo, puede tomar unos segundos...
  </v-snackbar>
  <v-snackbar
    v-model="emailError"
    :timeout="3000"
    color="error"
    rounded="pill"
  >
    Hubo un error enviando el correo. Por favor, inténtalo nuevamente.
  </v-snackbar>
  <v-snackbar
    v-model="whatsappSnackbarAlert"
    color="primary"
    rounded="pill"
  >
    Generando mensaje de WhatsApp...
  </v-snackbar>
  <v-snackbar
    v-model="errorSnackbarAlert"
    :timeout="3000"
    color="error"
    rounded="pill"
  >
    {{ errorMessage }}
  </v-snackbar>
</template>
